import { QueryClient } from '@tanstack/react-query';

import {
  useMembershipToolkitBlockContentQuery,
  useMembershipToolkitBlockGuidQuery,
} from '@module/mdrt-org/shared/utils/data/graphql/_generated/gql-generated';
import { getContentInCurrentLanguage } from '@module/mdrt-org/shared/utils/data/graphql/utils/get-content-in-current-language';
import { type ContentVisibilityOptions } from '@module/mdrt-org/shared/utils/types/visibility-options';

/* eslint-disable @typescript-eslint/no-explicit-any */
const simplifyObject = (object: any): any => {
  if (Array.isArray(object)) {
    return object.map(simplifyObject);
  } else if (object && typeof object === 'object') {
    const simplified: any = {};

    for (const key in object) {
      if (Object.prototype.hasOwnProperty.call(object, key)) {
        if (key === 'ContentLink' && object[key]?.Expanded) {
          Object.assign(simplified, simplifyObject(object[key].Expanded));
        } else if (key !== 'ContentLink' && key !== 'Expanded') {
          simplified[key] = simplifyObject(object[key]);
        }
      }
    }

    return simplified;
  }

  return object;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export type GenericToolkitLinkItem = {
  __typename: string;
  DisplayText: string;
  DownloadFileName: string;
  LinkToContent: string;
  ShouldDownload: boolean | null;
  Target: string;
};

export type DashboardCarouselCardToolkitLinkItem = ContentVisibilityOptions & {
  __typename: 'DashboardCarouselCardBlock';
  CardUrl: {
    DisplayText: string;
    LinkToContent: string;
    Target: string;
  };
  Description: string;
  ImageReference: {
    Url: string;
  };
  LocationsAndDates: [];
  Title: string;
};

export type LinksListItem = DashboardCarouselCardToolkitLinkItem | GenericToolkitLinkItem;

export type LinksList = ContentVisibilityOptions & {
  Items: LinksListItem[];
  MembershipYear: Nullable<number>;
  Title: string;
};

export type TabsData = {
  Description: string;
  key: string;
  Layout: '' | 'Images' | 'Links';
  Sections: LinksList[] | null;
  Title: string;
};

export const getMembershipToolkitContent = async () => {
  const queryClient = new QueryClient();

  const toolkitBlockGuidQueryData = await queryClient.fetchQuery({
    queryFn: useMembershipToolkitBlockGuidQuery.fetcher(),
    queryKey: useMembershipToolkitBlockGuidQuery.getKey(),
  });

  const toolkitBlockGuidQueryInCurrentLanguage = await getContentInCurrentLanguage(
    toolkitBlockGuidQueryData?.HomePageV2?.items
  );

  const toolkitBlockGuid = toolkitBlockGuidQueryInCurrentLanguage?.[0]?.AuthenticatedContent?.find(
    (item) => item?.ContentLink?.Expanded?.__typename === 'ToolkitBlock'
  )?.ContentLink?.GuidValue;

  const toolkitBlockContentQueryData = await queryClient.fetchQuery({
    queryFn: useMembershipToolkitBlockContentQuery.fetcher({ guid: toolkitBlockGuid ?? '' }),
    queryKey: useMembershipToolkitBlockContentQuery.getKey({ guid: toolkitBlockGuid ?? '' }),
  });

  const toolkitBlockContentQueryInCurrentLanguage = await getContentInCurrentLanguage(
    toolkitBlockContentQueryData?.ToolkitBlock?.items
  );

  const tabsData = (toolkitBlockContentQueryInCurrentLanguage?.[0]?.Tabs?.map(simplifyObject)?.map(
    (item, index) => ({
      ...item,
      key: item?.title?.toLocaleLowerCase().replaceAll(/\s+/gu, '-') + index || `item-${index}`,
    })
  ) ?? []) as TabsData[];

  return {
    anchorLink: toolkitBlockContentQueryInCurrentLanguage?.[0]?.AnchorLink || 'membership-kits',
    sectionDescription:
      toolkitBlockContentQueryInCurrentLanguage?.[0]?.Description ||
      'Your MDRT membership is very valuable — but not just to you. Share the value your clients receive as a result of the innovative ideas you can access as a member. Use the resources included in this toolkit to promote your practice and communicate what your membership means for your clients.',
    sectionTitle: toolkitBlockContentQueryInCurrentLanguage?.[0]?.Title || 'Membership kits',
    tabsData,
    visibilityOptions: {
      ...toolkitBlockContentQueryInCurrentLanguage?.[0],
    },
  };
};

export type MembershipToolkitContent = Awaited<
  Promise<PromiseLike<ReturnType<typeof getMembershipToolkitContent>>>
>;

export const isDashboardCarouselCardToolkitLinkItem = (
  item: LinksListItem
): item is DashboardCarouselCardToolkitLinkItem =>
  item.__typename === 'DashboardCarouselCardBlock' && 'CardUrl' in item;

export const isGenericToolkitLinkItem = (item: LinksListItem): item is GenericToolkitLinkItem =>
  item.__typename !== 'DashboardCarouselCardBlock' && 'DownloadFileName' in item;
